'use client'

import { Check, ChevronDown, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { comboboxOptionClass } from '@/components/widget/combobox-option'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { useCanAutoFocusSearch } from '@/hooks/use-screen'
import { cn } from '@/lib/utils'

interface HedgeUnderlyingSelectProps {
  value: string
  // Underlyings with an open position — listed first.
  options: string[]
  // Optional secondary group for callers that allow targets without a position.
  otherOptions?: string[]
  onChange: (value: string) => void
  /** Open positions per underlying, shown beside each option. */
  positionCounts?: Record<string, number>
  // Replaces the trigger's surface (bg/border/height/radius/text size) so a host form can
  // match its own fields; the dialog's compact filled chip is the default.
  fieldClassName?: string
}

// Searchable Target picker — same combobox popover pattern as hedge-instrument-select.tsx
// (portal into the surrounding dialog, Escape intercepted one level above Radix's own capture
// listener so it closes only the dropdown), but over a flat list of underlying strings split
// into two static groups (held vs. not) instead of one list of Instrument objects.
/** An underlying's coin mark ("HYPE_USDC" → HYPE). */
function AssetIcon({ asset }: { asset: string }) {
  return <CryptoIcon symbol={asset.split(/[_-]/)[0]} className="size-4 shrink-0 rounded-full" />
}

export function HedgeUnderlyingSelect({
  value,
  options,
  otherOptions = [],
  onChange,
  positionCounts,
  fieldClassName,
}: HedgeUnderlyingSelectProps) {
  const canAutoFocusSearch = useCanAutoFocusSearch()
  const t = useTranslations('trade.hedgeBuilder')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const activeItemRef = useRef<HTMLButtonElement>(null)
  const [container, setContainer] = useState<HTMLElement | null>(null)

  const filteredHeld = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((a) => a.toLowerCase().includes(q)) : options
  }, [options, query])

  const filteredOther = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? otherOptions.filter((a) => a.toLowerCase().includes(q)) : otherOptions
  }, [otherOptions, query])

  const flat = useMemo(() => [...filteredHeld, ...filteredOther], [filteredHeld, filteredOther])

  // The trigger label falls back to a placeholder for anything that isn't a real option — e.g.
  // the sentinel value the still-hidden Portfolio/Position-group targets use upstream.
  const knownValue = useMemo(
    () => (options.includes(value) || otherOptions.includes(value) ? value : ''),
    [options, otherOptions, value]
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      setOpen(false)
      setQuery('')
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [open])

  const choose = (asset: string) => {
    onChange(asset)
    setQuery('')
    setOpen(false)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const sel = flat[activeIndex]
      if (sel) choose(sel)
    }
  }

  const activeId = flat[activeIndex] ? `hedge-underlying-${flat[activeIndex]}` : undefined

  // Nothing to pick at all (the account holds no position): the trigger says so and the
  // popover says so in one line instead of offering an empty search.
  const isEmpty = options.length === 0 && otherOptions.length === 0

  const renderRow = (asset: string, idx: number) => {
    const isSelected = asset === value
    const isActive = idx === activeIndex
    const count = positionCounts?.[asset]
    return (
      <button
        key={asset}
        id={`hedge-underlying-${asset}`}
        ref={isActive ? activeItemRef : undefined}
        role="option"
        aria-selected={isSelected}
        type="button"
        tabIndex={-1}
        onMouseEnter={() => setActiveIndex(idx)}
        onClick={() => choose(asset)}
        className={comboboxOptionClass({ active: isActive, selected: isSelected })}
      >
        <span className="flex min-w-0 items-center gap-2">
          <AssetIcon asset={asset} />
          <span className="truncate">{asset}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {/* Just the number (the full "N positions" stays as its title): the words pushed the
              asset name into an ellipsis in a field-wide list. */}
          {count !== undefined && (
            <span
              title={t('positionCount', { count })}
              aria-label={t('positionCount', { count })}
              className="bg-foreground/5 text-foreground/50 min-w-4 rounded-xs px-1 text-center text-[10px] leading-4"
            >
              {count}
            </span>
          )}
          <Check className={cn('text-chart-mint size-3', !isSelected && 'invisible')} />
        </span>
      </button>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setContainer(triggerRef.current?.closest('[role="dialog"]') as HTMLElement | null)
        } else {
          setQuery('')
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            'flex w-full items-center justify-between gap-2 data-[state=open]:[&>svg]:rotate-180',
            fieldClassName ??
              'bg-foreground/10 data-[state=open]:bg-foreground/20 h-8 rounded-sm px-2.5 py-1 text-xs'
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {knownValue && <AssetIcon asset={knownValue} />}
            <span className={cn('truncate', !knownValue && 'text-foreground/40')}>
              {knownValue || (isEmpty ? t('noPositionsToHedge') : t('selectUnderlying'))}
            </span>
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-60 transition-transform" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        container={container ?? undefined}
        // At least the field's width, wider when a name needs it (a long underlying like
        // HYPE_USDC), so the list never truncates what it lists.
        className="w-auto max-w-64 p-0"
        style={{ minWidth: 'var(--radix-popover-trigger-width)' }}
      >
        {isEmpty ? (
          <p className="text-foreground/40 px-3 py-4 text-center text-xs">
            {t('noPositionsToHedge')}
          </p>
        ) : (
          <>
            <div className="border-foreground/10 flex items-center gap-2 border-b px-3 py-2">
              <Search className="text-muted-foreground size-3.5 shrink-0" />
              <input
                autoFocus={canAutoFocusSearch}
                role="combobox"
                aria-expanded
                aria-controls="hedge-underlying-list"
                aria-activedescendant={activeId}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('searchUnderlying')}
                className="placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div id="hedge-underlying-list" role="listbox" className="max-h-60 overflow-y-auto p-1">
              {flat.length === 0 ? (
                <p className="text-foreground/40 px-2 py-4 text-center text-xs">{t('noMatches')}</p>
              ) : (
                <>
                  {filteredHeld.map((a, idx) => renderRow(a, idx))}
                  {filteredHeld.length > 0 && filteredOther.length > 0 && (
                    <div className="bg-muted -mx-1 my-1 h-px" />
                  )}
                  {filteredOther.map((a, idx) => renderRow(a, filteredHeld.length + idx))}
                </>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
